// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import api from "@crm-src/lib/api"

import { crmApiUrl } from "./api"
import { tokenStore } from "../../lib/tokenStore.js"

/**
 * The two agent-only email-campaign audiences. "Qualified AI prospects" (the
 * sales agent's leads that were pushed INTO the CRM) are read straight from
 * crm-api by the picker and need no bridge — these two live only in the sales
 * agent's database, which crm-api cannot reach.
 *
 *   - sales_leads → named buyers on the agent's scored leads (OrgLead persons)
 *   - harvester   → the blind, industry-marked contact-harvester pool
 */
export type AiAudienceSource = "sales_leads" | "harvester"

/** crm-api's provenance tag per source (see the import_ai_audience endpoint). */
const IMPORT_SOURCE: Record<AiAudienceSource, string> = {
  sales_leads: "ai_sales_lead",
  harvester: "ai_harvester",
}

export interface AiAudienceContact {
  name: string
  email: string
  company: string
  job_title: string
  industry: string
  /** "unknown" | "guessed" | "likely" | "verified" — the agent's confidence. */
  email_confidence: string
}

export interface AiAudienceFetchResult {
  source: AiAudienceSource
  /** The agent's server-side total for the source (before email dedup). */
  total: number
  /** Deduped, email-only rows actually returned. */
  returned: number
  /** True when the audience hit the 10k per-import ceiling. */
  capped: boolean
  contacts: AiAudienceContact[]
}

/**
 * Fetch the sales agent's lead / harvester contacts for a campaign audience.
 *
 * Hits the DASHBOARD server (`/api/campaigns/ai-audience/*`), which holds the
 * agent creds + enforces the `crm:view` gate — NOT crm-api. So it goes straight
 * through `fetch` with the dashboard session token (like ai-prospect-release),
 * not the crm axios client (which mints a short-TTL crm-api token).
 */
export async function fetchAiAudience(source: AiAudienceSource): Promise<AiAudienceFetchResult> {
  const access = tokenStore.getAccess()
  const res = await fetch(crmApiUrl(`/api/campaigns/ai-audience/${source}`), {
    headers: access ? { Authorization: `Bearer ${access}` } : {},
    credentials: "include",
  })
  if (!res.ok) {
    let code = "AI_AUDIENCE_FETCH_FAILED"
    try {
      const body = (await res.json()) as { code?: string }
      if (body?.code) code = body.code
    } catch {
      /* non-JSON error body — keep the generic code */
    }
    throw new Error(code)
  }
  return (await res.json()) as AiAudienceFetchResult
}

/** A CRM contact group (the per-source list the import lands in). */
export interface AiAudienceGroup {
  id: number
  name: string
  members_count: number
}

interface ImportResponse {
  data?: { group?: AiAudienceGroup }
  meta?: { created: number; matched: number; skipped: number }
}

/**
 * Upsert ONE chunk of fetched AI contacts into the CRM directory + the
 * per-source contact group. crm-api dedupes by email and returns the group with
 * its live member count. Chunked by the caller so no single request is huge
 * (a harvester pool is thousands of contacts → the whole list at once would
 * blow the request body limit / time out). Uses the crm axios client (crm-api).
 */
async function importAiChunk(
  source: AiAudienceSource,
  contacts: AiAudienceContact[],
): Promise<ImportResponse> {
  const res = await api.post("/contacts/import_ai_audience", {
    source: IMPORT_SOURCE[source],
    contacts,
  })
  return res.data as ImportResponse
}

/** Per-request contact cap. Well under crm-api's 10k limit AND keeps each POST
 * body small (~1500 contacts ≈ 250 KB) so it never trips a proxy body limit. */
const IMPORT_CHUNK = 1500

export interface AiImportOutcome {
  /** The contact group the (sub)set landed in — selected as ONE recipient chip. */
  group: AiAudienceGroup | null
  created: number
  matched: number
}

/**
 * Land a set of already-fetched AI contacts (optionally ONE industry/vertical of
 * a pool) into the CRM directory + a group, returning the group for the picker
 * to select. Chunks the POST so a large set imports reliably; every chunk
 * appends to the same group. Passing `vertical` names a per-industry group
 * ("AI Harvester Contacts — Oil") so an operator can target one industry.
 */
export async function importAiContacts(
  source: AiAudienceSource,
  contacts: AiAudienceContact[],
  vertical?: string,
): Promise<AiImportOutcome> {
  let group: AiAudienceGroup | null = null
  let created = 0
  let matched = 0
  for (let i = 0; i < contacts.length; i += IMPORT_CHUNK) {
    const chunk = contacts.slice(i, i + IMPORT_CHUNK)
    const res = await api.post("/contacts/import_ai_audience", {
      source: IMPORT_SOURCE[source],
      ...(vertical ? { vertical } : {}),
      contacts: chunk,
    })
    const body = res.data as ImportResponse
    created += body.meta?.created ?? 0
    matched += body.meta?.matched ?? 0
    // The LAST chunk's group carries the cumulative member count (crm-api
    // recomputes it per call after the membership insert).
    if (body.data?.group) group = body.data.group
  }
  return { group, created, matched }
}

export interface AiIndustry {
  /** The agent's raw vertical ("oil"), passed back to the import for grouping. */
  vertical: string
  /** Title-cased for display ("Oil"). */
  label: string
  count: number
}

/** Group a fetched audience by the agent's vertical, busiest first, for the
 * per-source "by industry" picker. Blank verticals bucket under "Other". */
export function aiIndustryBreakdown(contacts: AiAudienceContact[]): AiIndustry[] {
  const counts = new Map<string, number>()
  for (const c of contacts) {
    const v = (c.industry || "").trim().toLowerCase() || "other"
    counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([vertical, count]) => ({
      vertical,
      label: vertical === "other" ? "Other" : vertical.replace(/\b\w/g, (m) => m.toUpperCase()),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/** The contacts of a fetched audience in one vertical (matches `aiIndustryBreakdown`). */
export function aiContactsForVertical(contacts: AiAudienceContact[], vertical: string): AiAudienceContact[] {
  return contacts.filter((c) => ((c.industry || "").trim().toLowerCase() || "other") === vertical)
}
